import { z } from 'zod'
import { dataNominal, uuidDeRota } from './comum.js'

/** Fila única da leitura bancária: o botão da tela 6.2 e o planejador publicam aqui. */
export const FILA_SINCRONIZAR_SICOOB = 'dv-sincronizar-sicoob'

/**
 * Fila do PLANEJADOR (D-160). O worker agenda; o planejador escolhe as contas elegíveis e
 * publica na fila acima. Ele nunca fala com o banco e não recebe certificado.
 */
export const FILA_PLANEJAR_SICOOB = 'dv-sicoob-planejar'

/**
 * Fila da VERIFICAÇÃO da integração (certificado, leitura parada, falha, bloqueio). O worker
 * agenda; quem consome é a API, porque é ela que tem o certificado para ler a validade.
 */
export const FILA_VERIFICAR_SICOOB = 'dv-sicoob-verificar'

/**
 * De onde veio a leitura. `manual` é o botão; `job` é o planejador; `backfill` é a carga de
 * meses escolhidos à mão (a rota de importação e a linha de comando).
 */
export const origemDaLeituraSicoob = z.enum(['manual', 'job', 'backfill'])
export type OrigemDaLeituraSicoob = z.infer<typeof origemDaLeituraSicoob>

/**
 * O job leva somente identidades internas. Credenciais e períodos são resolvidos no
 * consumidor, no instante da execução, e nunca são persistidos no pg-boss. A `origem` é
 * opcional para os jobs que já estavam na fila antes dela existir: esses são do botão.
 */
export const jobDeSincronizacaoSicoob = z.object({
  entityId: uuidDeRota,
  contaBancariaId: uuidDeRota,
  origem: origemDaLeituraSicoob.optional(),
})
export type JobDeSincronizacaoSicoob = z.infer<typeof jobDeSincronizacaoSicoob>

/** `canario` é o disparo controlado da publicação: ignora os controles de ativação. */
export const cadenciaDaLeituraSicoob = z.enum(['diaria', 'intradiaria', 'canario'])
export type CadenciaDaLeituraSicoob = z.infer<typeof cadenciaDaLeituraSicoob>

export const jobDePlanejamentoSicoob = z.object({
  cadencia: cadenciaDaLeituraSicoob,
})
export type JobDePlanejamentoSicoob = z.infer<typeof jobDePlanejamentoSicoob>

/**
 * A agenda da leitura automática (D-160), no relógio de São Paulo. É a fonte ÚNICA: dela
 * saem a expressão cron do worker e a "próxima leitura prevista" da tela.
 */
export const FUSO_DA_LEITURA_SICOOB = 'America/Sao_Paulo'
export const AGENDA_DA_LEITURA_SICOOB = {
  diaria: { chave: 'diaria', horas: [6], diasDaSemana: [1, 2, 3, 4, 5, 6, 7] },
  intradiaria: { chave: 'intradiaria', horas: [8, 10, 12, 14, 16, 18, 20], diasDaSemana: [1, 2, 3, 4, 5] },
} as const

/** A verificação roda de hora em hora, fora da hora cheia das leituras. */
export const CRON_DA_VERIFICACAO_SICOOB = '17 * * * *'

/**
 * Os controles de ativação (D-160), em `parametro_sistema` e editáveis na tela de
 * Parâmetros. Nascem desligados: a automação só liga depois do canário em produção.
 */
export const PARAMETRO_LEITURA_DIARIA_SICOOB = 'sicoob.leitura_diaria_ativa'
export const PARAMETRO_LEITURA_INTRADIARIA_SICOOB = 'sicoob.leitura_intradiaria_ativa'

/**
 * Os códigos de resultado de uma tentativa. Cada um vira uma frase na tela; nenhum carrega
 * segredo, número de conta ou resposta crua do banco.
 */
export const codigoDaFalhaSicoob = z.enum([
  'sicoob.certificado_recusado',
  'sicoob.certificado_vencido',
  'sicoob.credencial_indisponivel',
  'sicoob.autenticacao_recusada',
  'sicoob.indisponivel',
  'sicoob.divergencia',
  'sicoob.contrato',
  'interno',
])
export type CodigoDaFalhaSicoob = z.infer<typeof codigoDaFalhaSicoob>

/** Falha que não passa sozinha: suspende a automação até alguém corrigir e ler de novo. */
export const FALHAS_PERMANENTES_SICOOB: readonly CodigoDaFalhaSicoob[] = [
  'sicoob.certificado_recusado',
  'sicoob.certificado_vencido',
  'sicoob.credencial_indisponivel',
]

/**
 * Divergência bancária (D-162): o que a leitura encontrou e uma pessoa precisa decidir.
 *
 * - `conteudo_alterado`: o banco mudou um lançamento já conhecido.
 * - `ausencia`: um lançamento conhecido sumiu de duas leituras de mês fechado.
 * - `identificador_novo`: um identificador novo trouxe o mesmo conteúdo de um lançamento
 *   conhecido que sumiu da mesma leitura.
 *
 * O lançamento tardio no meio de um dia já lido NÃO é divergência: com o identificador do
 * banco, a releitura reconhece o lançamento pela identidade, e não pela posição no dia.
 */
export const tipoDeDivergenciaSicoob = z.enum(['conteudo_alterado', 'ausencia', 'identificador_novo'])
export type TipoDeDivergenciaSicoob = z.infer<typeof tipoDeDivergenciaSicoob>

/** `superada`: a condição deixou de existir numa leitura seguinte; nada mudou no razão. */
export const estadoDaDivergenciaSicoob = z.enum([
  'aberta',
  'aceita',
  'mantida',
  'ausencia_reconhecida',
  'superada',
])
export type EstadoDaDivergenciaSicoob = z.infer<typeof estadoDaDivergenciaSicoob>

export const acaoSobreDivergenciaSicoob = z.enum(['aceitar', 'manter', 'reconhecer_ausencia'])
export type AcaoSobreDivergenciaSicoob = z.infer<typeof acaoSobreDivergenciaSicoob>

/** Uma versão do lançamento como a tela mostra: só o que a pessoa precisa comparar. */
export const versaoDoLancamentoSicoob = z.object({
  data: z.string(),
  valor: z.string(),
  direcao: z.enum(['credito', 'debito']),
  historico: z.string().nullable(),
  documento: z.string().nullable(),
  contraparteNome: z.string().nullable(),
  contraparteDocumento: z.string().nullable(),
})
export type VersaoDoLancamentoSicoob = z.infer<typeof versaoDoLancamentoSicoob>

export const divergenciaSicoob = z.object({
  id: uuidDeRota,
  tipo: tipoDeDivergenciaSicoob,
  estado: estadoDaDivergenciaSicoob,
  contaBancariaId: uuidDeRota,
  contaNome: z.string(),
  /** Mês do lançamento, `AAAA-MM`. */
  periodo: z.string(),
  registrada: versaoDoLancamentoSicoob.nullable(),
  recebida: versaoDoLancamentoSicoob.nullable(),
  /** Nomes dos campos que diferem entre a registrada e a recebida. */
  camposQueDiferem: z.array(z.string()),
  /** O que acontece se a pessoa aceitar — em uma frase, para a tela. */
  consequencia: z.string(),
  acoes: z.array(acaoSobreDivergenciaSicoob),
  /** O movimento está conciliado: aceitar a versão nova exige desfazer a conciliação antes. */
  movimentoConciliado: z.boolean(),
  detectadaEm: z.string(),
  resolvidaEm: z.string().nullable(),
  resolvidaPor: z.string().nullable(),
})
export type DivergenciaSicoob = z.infer<typeof divergenciaSicoob>

export const entradaDeResolucaoDeDivergencia = z.object({
  acao: acaoSobreDivergenciaSicoob,
  observacao: z.string().trim().max(500).optional(),
})
export type EntradaDeResolucaoDeDivergencia = z.infer<typeof entradaDeResolucaoDeDivergencia>

/**
 * DS-621 (D-175): a divergência do extrato de investimento — a versão em quarentena que não é só o saldo líquido
 * recalculado (0157) —, ao lado da versão vigente, para uma pessoa decidir: aceitar a do banco ou manter a registrada.
 */
export const divergenciaDeInvestimento = z.object({
  linhaId: uuidDeRota,
  conta: z.string(),
  idAplicacao: z.string(),
  dataAplicacao: dataNominal,
  ordem: z.number().int(),
  dataLote: dataNominal,
  historico: z.string(),
  creditos: z.string(),
  debitos: z.string(),
  vigenteDataLote: dataNominal.nullable(),
  vigenteHistorico: z.string().nullable(),
  vigenteCreditos: z.string().nullable(),
  vigenteDebitos: z.string().nullable(),
  detectadaEm: dataNominal,
  /** A linha já virou movimento da conta de aplicação: só se mantém a registrada. */
  jaVirouMovimento: z.boolean(),
})
export type DivergenciaDeInvestimento = z.infer<typeof divergenciaDeInvestimento>

export const decisaoSobreInvestimento = z.enum(['aceita', 'mantida'])
export type DecisaoSobreInvestimento = z.infer<typeof decisaoSobreInvestimento>

export const entradaDeDecisaoDeInvestimento = z.object({
  decisao: decisaoSobreInvestimento,
  motivo: z.string().trim().min(3, 'Diga o motivo da decisão.').max(500),
})
export type EntradaDeDecisaoDeInvestimento = z.infer<typeof entradaDeDecisaoDeInvestimento>

/** DS-621 (D-175): o IRRF retido em aplicações no período, no razão e sem classificação, para a futura apuração. */
export const irrfRetidoEmAplicacoes = z.object({
  de: dataNominal,
  ate: dataNominal,
  total: z.string(),
  linhas: z.array(z.object({ data: dataNominal, valor: z.string(), conta: z.string() })),
})
export type IrrfRetidoEmAplicacoes = z.infer<typeof irrfRetidoEmAplicacoes>
